import { useState, useEffect } from "react";
import ReactGA from "react-ga";

import elementConfigs from "../data/elementConfigs.json";
import moleculeRegistry from "../data/moleculeRegistry.generated.json";
import elementsData from "../assets/elements.json";

/*
░█▀▀█ ░█─── ░█▀▀▀█ ░█▀▀█ ─█▀▀█ ░█─── ░█▀▀▀█ 
░█─▄▄ ░█─── ░█──░█ ░█▀▀▄ ░█▄▄█ ░█─── ─▀▀▀▄▄ 
░█▄▄█ ░█▄▄█ ░█▄▄▄█ ░█▄▄█ ░█─░█ ░█▄▄█ ░█▄▄▄█
*/

export const CANVAS = {
  WIDTH: "fit-parent",
  HEIGHT: "85vh",
};

export const RENDERER = {
  ATOM_RADIUS: "0.3",
  TUBE_RADIUS: "0.3",
};

export const isTouchDevice = () => {
  const nav = navigator as Navigator & { msMaxTouchPoints?: number };
  return (
    "ontouchstart" in window ||
    nav.maxTouchPoints > 0 ||
    (nav.msMaxTouchPoints ?? 0) > 0
  );
};

export const capitalize = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

/** Curriculum molecules: synced with server/molecule_registry.py (run sync script after edits). */
export const moleculeDict = Object.fromEntries(
  Object.entries(moleculeRegistry).map(([formula, m]) => [
    formula,
    m.legacyRow,
  ])
);

export const moleculesWithMolecularOrbitals = Object.entries(moleculeRegistry)
  .filter(([, m]) => m.moViewer)
  .map(([k]) => k);

export const organicMolecules = Object.entries(moleculeRegistry)
  .filter(([, m]) => m.organicHighlight)
  .map(([k]) => k);


export const bondShapeDict = {
  H2O: [[0, 1], [0, 2]],
  H2: [[0, 1]],
  Cl2: [[0, 1]],
  HCl: [[0, 1]],
};

export const atomDict = {
  H: [
    "H",
    "Hydrogen Atom",
    "Atom",
    "Lightest element. Gas of diatomic molecules. Highly combustible.",
    "1s^1",
  ],
  Be: [
    "Be",
    "Beryllium Atom",
    "Atom",
    "Steel-gray, strong, lightweight and brittle alkaline earth metal.",
    "[He] 2s^2",
  ],
  B: [
    "B",
    "Boron Atom",
    "Atom",
    "Brittle, dark, lustrous metalloid.",
    "[He] 2s^2 2p^1",
  ],
  O: [
    "O",
    "Oxygen Atom",
    "Atom",
    "Colorless, odorless, tasteless gas essential to living organisms.",
    "[He] 2s^2 2p^4",
  ],
  F: [
    "F",
    "Fluorine Atom",
    "Atom",
    "Highly reactive pale yellow diatomic gas. Most electronegative element.",
    "[He] 2s^2 2p^5",
  ],
  Ne: [
    "Ne",
    "Neon Atom",
    "Atom",
    "Colorless, odorless, inert monatomic gas.",
    "[He] 2s^2 2p^6",
  ],
  Fe: [
    "Fe",
    "Iron Atom",
    "Atom",
    "Most common element on Earth. Belongs to first transition series and group 8 of periodic table.",
    "[Ar] 4s^2 3d^6",
  ],
  Co: [
    "Co",
    "Cobalt Atom",
    "Atom",
    "Hard, lustrous, silver-gray metal.",
    "[Ar] 4s^2 3d^7",
  ],
  Ni: [
    "Ni",
    "Nickel Atom",
    "Atom",
    "Silvery-white lustrous metal with a slight golden tinge.",
    "[Ar] 4s^2 3d^8",
  ],
  Cu: [
    "Cu",
    "Copper Atom",
    "Atom",
    "Soft, malleable, and ductile metal with high thermal and electrical conductivity.",
    "[Ar] 4s^1 3d^10",
  ],
  Zn: [
    "Zn",
    "Zinc Atom",
    "Atom",
    "First element in group 12 of the periodic table. Chemically similar to magnesium.",
    "[Ar] 4s^2 3d^10",
  ],
  Pd: [
    "Pd",
    "Palladium Atom",
    "Atom",
    "Rare and lustrous silvery-white metal with greatest density of any element.",
    "[Kr] 4d^10",
  ],
  Ce: [
    "Ce",
    "Cerium Atom",
    "Atom",
    "Soft, ductile, and silvery-white metal that tarnishes when exposed to air.",
    "[Xe] 6s^2 4f^1",
  ],
  Pr: [
    "Pr",
    "Praseodymium Atom",
    "Atom",
    "Soft and traditionally considered to be one of the rare-earth elements.",
    "[Xe] 6s^2 4f^3",
  ],
  Nd: [
    "Nd",
    "Neodymium Atom",
    "Atom",
    "Fourth member of the lanthanide series and traditionally considered to be one of the rare-earth elements.",
    "[Xe] 6s^2 4f^4",
  ],
  Pm: [
    "Pm",
    "Promethium Atom",
    "Atom",
    "Fifth member of the lanthanide series and traditionally considered to be one of the rare-earth elements.",
    "[Xe] 6s^2 4f^5",
  ],
  Sm: [
    "Sm",
    "Samarium Atom",
    "Atom",
    "Sixth member of the lanthanide series and traditionally considered to be one of the rare-earth elements.",
    "[Xe] 6s^2 4f^6",
  ],
  Eu: [
    "Eu",
    "Europium Atom",
    "Atom",
    "Soft, silvery-white metal that tarnishes when exposed to air.",
    "[Xe] 6s^2 4f^7",
  ],
  Gd: [
    "Gd",
    "Gadolinium Atom",
    "Atom",
    "Silvery-white, malleable, and ductile rare earth metal. Third most abundant rare earth element.",
    "[Xe] 6s^2 5d^1 4f^7",
  ],
  Li: [
    "Li",
    "Lithium Atom",
    "Atom",
    "Composed of three electrons bound to a nucleus containing three protons. Common alkali metal.",
    "1s^2 2s^1",
  ],
  Na: [
    "Na",
    "Sodium Atom",
    "Atom",
    "Very soft silvery-white metal. Sixth most abundant element on Earth.",
    "[Ne] 3s^1",
  ],
  K: [
    "K",
    "Potassium Atom",
    "Atom",
    "Silvery-white metal that is soft enough to be cut with a knife with little force. Reacts rapidly with atmospheric oxygen.",
    "[Ar] 4s^1",
  ],
};

/** Row passed into Renderer selection: symbol, label, kind, blurb, electron configuration string. */
export type AtomSelectionRow = readonly [
  string,
  string,
  string,
  string,
  string,
];

/** Symbol → full element name lookup from elements.json */
const _elementNameMap: Record<string, string> = {};
(elementsData as { symbol: string; name: string }[]).forEach((e) => {
  _elementNameMap[e.symbol] = e.name;
});

/** Resolve periodic-table symbol to Table/Renderer row; uses atomDict or elementConfigs fallback. */
export const buildAtomSelection = (sym: string): AtomSelectionRow | null => {
  const symbol = (sym || "").trim();
  if (!symbol) return null;
  const fromDict = atomDict[symbol as keyof typeof atomDict];
  if (fromDict) return [...fromDict] as unknown as AtomSelectionRow;
  const cfg = elementConfigs[symbol as keyof typeof elementConfigs];
  if (!cfg) return null;
  const fullName = _elementNameMap[symbol] || symbol;
  return [
    symbol,
    `${fullName} Atom`,
    "Atom",
    `${fullName} (${symbol}): interactive atomic orbital visualization.`,
    cfg,
  ];
};

export const normalizeData = (val, max, min) => {
  /*
    This function normalizes a given dataset within a certain range that is defined
  
    Parameters
    ----------
    val: Float
      A value that has to be normalized within a range
    max: Float
      The desired upper limit of the value
    min: Float
      The desired lower limit of the value
  
    Returns
    -------
    Float
      Returns a normalized float value contained within the boundary set
    */

  return (val - min) / (max - min);
};

export const getMoleculeColour = (
  element,
  volume,
) => {
  /*
    Getting the molecule colour based upon the element name and the volume information
  
    Parameters
    ----------
    element: String
        Name of the element
    volume: Float
        Volume that correspond with each coordinate of the Canvas
  
    Returns
    -------
    String
        Contains the RGB information of the generated colour
    */
  switch (element) {
    case "H":
      return `hsl(${volume * 100.0 + 800.0}, 100%, 60%)`;

    case "O":
      return `hsl(${volume * 1500.0 + 200.0}, 100%, 60%)`;

    case "H2":
      return `hsl(${volume * 100.0 + 800.0}, 100%, 60%)`;

    case "Cl2":
      return `hsl(${volume * 204000.0 + 200.0}, 100%, 60%)`;

    case "H2O":
      return `hsl(${volume * 5000.0 + 180.0}, 100%, 60%)`;

    case "HCl":
      return `hsl(${volume * 204000.0 + 200.0}, 100%, 60%)`;

    case "CH3OH":
    case "C6H6":
    case "C2H4":
      return `hsl(${volume * 5000.0 + 180.0}, 100%, 60%)`;

    default:
      return `hsl(${volume * 100.0 + 800.0}, 100%, 60%)`;
  }
};

export const getAtomColour = (element) => {
  switch (element) {
    case "H":
      return "#FFFFFF"; // light white for the lack of visible flame color
    case "Be":
      return "#FFB266"; // Be does not have a characteristic flame color, but let's keep the peachy orange
    case "B":
      return "#00FF00"; // bright green for the characteristic green flame of boron compounds
    case "Li":
      return "#FF4500"; // bright red for the characteristic crimson flame of lithium
    case "Na":
      return "#FFFF00"; // bright yellow for the characteristic yellow flame of sodium
    case "K":
      return "#9B30FF"; // light purple for the characteristic lilac/purple flame of potassium
    case "O":
      return "#F07070"; // keeping bright red for high reactivity, as oxygen does not show color in a flame test
    case "F":
      return "#66CC99"; // keeping green as fluorine doesn't have a characteristic flame color either
    case "Ne":
      return "#EBEBEB"; // keeping light gray for its low reactivity and absence of flame color
    case "Fe":
      return "#D9A871"; // golden brown for the typical orange sparks produced by iron
    case "Co":
      return "#FF5C5C"; // dark red, keeping it as cobalt doesn't have a distinct flame color
    case "Ni":
      return "#A9A9A9"; // dark gray, as nickel does not have a characteristic flame color
    case "Cu":
      return "#00FF7F"; // bright green for the characteristic green/blue flame of copper
    case "Zn":
      return "#AED6F1"; // light blue for its zinc vapor color in a flame
    case "Pd":
      return "#E8E8E8"; // light gray, as palladium does not have a characteristic flame color
    case "Ce":
      return "#FFDAB9"; // peach, as cerium does not have a strong characteristic flame color
    case "Pr":
      return "#FF9966"; // coral, as praseodymium doesn't have a strong characteristic flame color
    case "Nd":
      return "#A0522D"; // sienna, for neodymium, maintaining the previous color as it does not have a strong flame color
    case "Pm":
      return "#FFA07A"; // light salmon, for promethium, maintaining as it doesn't have a characteristic flame color
    case "Sm":
      return "#FFD700"; // gold for the yellow flame of samarium
    case "Eu":
      return "#CD5C5C"; // indian red, for the strong red flame of europium
    case "Gd":
      return "#87CEFA"; // light sky blue, keeping it as gadolinium does not have a characteristic flame color
    default:
      return "#C8C8C8"; // light gray for unknown elements
  }
};

export const getMoleculeOpacity = (element, volume) => {
  /*
    Getting the molecule opacity based upon the element name and the volume information
  
    Parameters
    ----------
    element: String
        Name of the element
    volume: Float
        Volume that correspond with each coordinate of the Canvas
  
    Returns
    -------
    String
        Contains the normalized value of the volume so that it can be used for the opacity
    */
  switch (element) {
    case "H2":
      return normalizeData(volume, 1, 0) / 50;

    case "Cl2":
      return normalizeData(255 - Math.abs(volume) * 1.004, 1, -1);

    case "H2O":
      return normalizeData(255 - Math.abs(volume) * 1.006, 1, -1);

    case "HCl":
      return normalizeData(255 - Math.abs(volume) * 1.003, 1, -1);

    default:
      return normalizeData(volume, 1, 0) / 50;
  }
};

export const getCameraPosition = (element) => {
  /*
    Updating the camera position according to the element name
  
    Parameters
    ----------
    element: String
        Name of the element
  
    Returns
    -------
    Dictionary
        Contains the position values and the FOV of the camera
    */
  switch (element) {
    case "H":
      return { fov: 15, position: [-6.25, 10, 10] };

    case "Na":
      return { fov: 55, position: [-6.25, 10, 10] };

    case "K":
      return { fov: 65, position: [-6.25, 10, 10] };

    case "H2":
      return { fov: 20, position: [-6.25, 10, 10] };

    case "H2O":
      return { fov: 55, position: [-6.25, 10, 10] };

    case "HCl":
      return { fov: 55, position: [-6.25, 10, 10] };

    case "CH3OH":
    case "C6H6":
    case "C2H4":
      return { fov: 75, position: [-6.25, 10, 10] };

    case "Cl2":
      return { fov: 75, position: [-6.25, 10, 10] };

    default:
      return { fov: 35, position: [-6.25, 10, 10] };
  }
};

export function useWindowSize() {
  /*
    Initialize state with undefined width/height so server and client renders match

    Parameters
    ----------
    None

    Returns
    -------
    Array
        Contains the x and y sizes of the current window
    */

  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    // only execute all the code below in client side
    if (typeof window !== "undefined") {
      // Handler to call on window resize
      function handleResize() {
        // Set window width/height to state
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      }

      // Add event listener
      window.addEventListener("resize", handleResize);

      // Call handler right away so state gets updated with initial window size
      handleResize();

      // Remove event listener on cleanup
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []); // Empty array ensures that effect is only run on mount
  return windowSize;
}

export const zoomInCamera = (camera, constant) => {
  camera.position.x = camera.position.x / constant;
  camera.position.y = camera.position.y / constant;
  camera.position.z = camera.position.z / constant;
};

export const zoomOutCamera = (camera, constant) => {
  camera.position.x = camera.position.x * constant;
  camera.position.y = camera.position.y * constant;
  camera.position.z = camera.position.z * constant;
};

export const useAnalyticsEventTracker = (category = "Renderer") => {
  const eventTracker = (
    action = "Initiate Rendering Process",
    label = "Renderer"
  ) => {
    ReactGA.event({ category, action, label });
  };
  return eventTracker;
};

export const isElectron = () => {
  // Renderer process
  if (
    typeof window !== "undefined" &&
    typeof window.process === "object" &&
    window.process.type === "renderer"
  ) {
    return true;
  }

  // Main process
  if (
    typeof process !== "undefined" &&
    typeof process.versions === "object" &&
    !!process.versions.electron
  ) {
    return true;
  }

  // Detect the user agent when the `nodeIntegration` option is set to true
  if (
    typeof navigator === "object" &&
    typeof navigator.userAgent === "string" &&
    navigator.userAgent.indexOf("Electron") >= 0
  ) {
    return true;
  }

  return false;
};

export const padZero = (str: string, len = 2): string => {
  const zeros = new Array(len).join("0");
  return (zeros + str).slice(-len);
};

export const invertColor = (hex: string): string => {
  let h = hex;
  if (h.indexOf("#") === 0) {
    h = h.slice(1);
  }
  if (h.length === 3) {
    h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  }
  if (h.length !== 6) {
    throw new Error("Invalid HEX color.");
  }
  const r = (255 - parseInt(h.slice(0, 2), 16)).toString(16);
  const g = (255 - parseInt(h.slice(2, 4), 16)).toString(16);
  const b = (255 - parseInt(h.slice(4, 6), 16)).toString(16);
  return "#" + padZero(r) + padZero(g) + padZero(b);
};